import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { canManageClubs } from '../utils/auth';
import '../App.css';

interface Club {
  id: string;
  name: string;
  logo?: string;
  colors: { primary: string; secondary: string };
  membershipFee?: number;
  administratorId: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const Clubs: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchClubs(currentPage);
  }, [currentPage]);

  const fetchClubs = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/clubs', {
        params: {
          page: page.toString(),
          limit: '100'
        }
      });
      
      if (response.data.clubs) {
        setClubs(response.data.clubs);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old API format
        setClubs(response.data);
        setPagination({
          page: 1,
          limit: 100,
          total: response.data.length,
          totalPages: Math.ceil(response.data.length / 100)
        });
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && clubs.length === 0) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Clubs</h1>
        {canManageClubs(user?.profileType) && (
          <button className="btn btn-primary" onClick={() => navigate('/clubs/create')}>
            Create New Club
          </button>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h2>All Clubs ({pagination.total} clubs)</h2>
          {pagination.totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {clubs.length === 0 ? (
          <p>No clubs found.</p>
        ) : (
          <>
            <div className="grid">
              {clubs.map((club) => (
                <div
                  key={club.id}
                  className="card"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/clubs/${club.id}`)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                    {club.logo ? (
                      <img src={club.logo} alt={club.name} style={{ width: '60px', height: '60px', borderRadius: '12px' }} />
                    ) : (
                      <div
                        style={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '12px',
                          backgroundColor: club.colors.primary,
                          border: `3px solid ${club.colors.secondary}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '24px',
                          fontWeight: 'bold'
                        }}
                      >
                        {club.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3>{club.name}</h3>
                      {club.membershipFee && (
                        <p style={{ color: '#757575', fontSize: '14px' }}>
                          Membership Fee: ${club.membershipFee}
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <Link
                      to={`/clubs/${club.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="btn btn-primary"
                      style={{ fontSize: '14px', padding: '8px 15px' }}
                    >
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            {pagination.totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages} (Showing {clubs.length} of {pagination.total} clubs)
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= pagination.totalPages}
                >
                  Next
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={currentPage >= pagination.totalPages}
                >
                  Last
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Clubs;

